import React, { useState, useRef, useEffect } from "react";
import InnoIcons from "@InnoIcons/";
import SC from "./style";
import { InputField } from "@components/";
import theme from "@theme/";

const SizeFilterDropdown = ({
  label = "File Size",
  selectedHeaderId,
  onHeaderChange,
  selectedValue = "",
  headerOptionsValueName = "File Size",
  onValueChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [unit, setUnit] = useState("MB");
  const [inputValue, setInputValue] = useState(selectedValue);
  const dropdownRef = useRef();

  // Operator options
  const headerOptions = [
    { id: "equals", label: `${headerOptionsValueName} = (equals)` },
    { id: "lessThanEqual", label: `${headerOptionsValueName} <= (equals)` },
    { id: "greaterThanEqual", label: `${headerOptionsValueName} >= (equals)` },
  ];

  const selectedHeader = headerOptions.find((h) => h.id === selectedHeaderId);

  // Convert input value to KB before sending to backend
  const convertToKB = (value, unit) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    switch (unit) {
      case "KB":
        return num;
      case "MB":
        return num * 1024;
      case "GB":
        return num * 1024 * 1024;
      default:
        return num;
    }
  };

  const handleUpdate = () => {
    const valueInKB = convertToKB(inputValue, unit);

    // Prevent sending empty/invalid values
    if (!inputValue || isNaN(valueInKB) || valueInKB <= 0) {
      onValueChange?.(null); // reset instead of passing 0
    } else {
      onValueChange?.(valueInKB);
    }

    setIsOpen(false);
    setIsHeaderExpanded(false);
  };

  const handleUnitChange = (e) => setUnit(e.target.value);
  const handleInputChange = (name, val) => {
    if (/^\d*\.?\d*$/.test(val)) setInputValue(val);
  };

  const handleHeaderClick = (headerId) => {
    onHeaderChange(headerId);
    setIsHeaderExpanded(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setIsHeaderExpanded(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayValue =
    unit === "MB"
      ? (selectedValue / 1024).toFixed(2)
      : unit === "GB"
      ? (selectedValue / (1024 * 1024)).toFixed(2)
      : selectedValue;

  return (
    <SC.DropdownContainer ref={dropdownRef}>
      <SC.DropdownButton
        isOpen={isOpen}
        isActive={!!selectedValue && Number(selectedValue) > 0}
        onClick={() => setIsOpen((p) => !p)}
      >
        {selectedValue && Number(selectedValue) > 0
          ? `${label}: ${
              selectedHeader?.id === "equals"
                ? "="
                : selectedHeader?.id === "lessThanEqual"
                ? "<="
                : selectedHeader?.id === "greaterThanEqual"
                ? ">="
                : ""
            } ${displayValue} ${unit}`
          : `${label}`}
        <SC.ChevronIcon
          isOpen={isOpen}
          isActive={!!selectedValue && Number(selectedValue) > 0}
        />
      </SC.DropdownButton>

      {isOpen && (
        <SC.DropdownMenu isOpen={isOpen}>
          {/* Header/Operator */}
          <SC.DropdownHeader
            isClickable
            onClick={() => setIsHeaderExpanded((p) => !p)}
          >
            <SC.HeaderText>
              {selectedHeader?.label || "Select Condition"}
            </SC.HeaderText>
            <SC.HeaderChevron isExpanded={isHeaderExpanded} />
          </SC.DropdownHeader>

          <SC.HeaderOptionsContainer isVisible={isHeaderExpanded}>
            {headerOptions.map((header) => (
              <SC.HeaderOption
                key={header.id}
                isSelected={header.id === selectedHeaderId}
                onClick={() => handleHeaderClick(header.id)}
              >
                {header.label}
              </SC.HeaderOption>
            ))}
          </SC.HeaderOptionsContainer>

          {/* Input + Unit */}
          <div
            style={{ display: "flex", alignItems: "center", margin: "30px 0" }}
          >
            <InputField
              name="fileSize"
              type="number"
              placeholder="Enter size"
              value={inputValue}
              onChange={handleInputChange}
              style={{ width: "45%", height: "34px", borderRadius: "5px" }}
            />
            <SC.UnitSelect value={unit} onChange={handleUnitChange}>
              <option value="KB">KB</option>
              <option value="MB">MB</option>
              <option value="GB">GB</option>
            </SC.UnitSelect>
          </div>

          {/* Footer */}
          <SC.DropdownFooter
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <SC.UpdateButton
              onClick={handleUpdate}
              style={{
                background: theme.gradient.themeGradient,
                color: "#fff",
              }}
            >
              Update
            </SC.UpdateButton>

            <SC.ClearAllButton
              onClick={() => {
                setInputValue(""); // clear input field
                onValueChange?.(null); // reset parent filter
                setIsOpen(false); // close dropdown
                setIsHeaderExpanded(false);
              }}
            >
              Clear
            </SC.ClearAllButton>
          </SC.DropdownFooter>
        </SC.DropdownMenu>
      )}
    </SC.DropdownContainer>
  );
};

export default SizeFilterDropdown;
